import { JSX, memo, useContext, useMemo } from 'react';
import { FilterContext } from '@app/projects/network-assessment/maps/network-coverage-map/contexts/FilterContext';
import { NetworkCoverageMapDataContext } from '@app/projects/network-assessment/maps/network-coverage-map/contexts/NetworkCoverageMapDataContext';
import { FilterType, ProjectRunConfigurationType, RegionSetStatusType } from '@app/projects/enumerators';
import 'mapbox-gl/dist/mapbox-gl.css';
import { NestingParentDropdownItem } from '@app/shared/checkbox-dropdown/types';
import SingleSelectDropdown from '@app/shared/checkbox-dropdown/SingleSelectDropdown';
import { CheckboxStateEnum } from '@app/shared/qes-standards/forms/Checkbox';
import { createPortal } from 'react-dom';
import { useMapboxControl } from '@app/shared/mapping/useMapBoxControl';
import RoundCheckbox from "@app/shared/RoundCheckbox";
import { FilterControlProps } from '@app/projects/network-assessment/maps/network-coverage-map/interfaces';
import { NetworkCoverageMapMembershipType } from '@app/projects/network-assessment/maps/network-coverage-map/enumerators';
import NetworkCoverageMapMembershipTypeFilter from '@app/projects/network-assessment/maps/network-coverage-map/controls/NetworkCoverageMapMembershipTypeFilter';
import { getPrimaryAndSecondaryRunConfigurations } from '@app/projects/network-assessment/adequacy/utility';
import DualRangeSlider from '@app/shared/DualRangeSlider';
import { HUNDRED_PERCENT_FILTER, MAX_TIMESLICES, MAX_TIMESLICES_WITH_UNPUBLISHED, ZERO_PERCENT_FILTER } from '@app/projects/network-assessment/maps/network-coverage-map/NetworkCoverageMapsConstants';
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import ProjectRunConfiguration from '@app/projects/projectRunConfiguration';
import { ProviderNetworkTypeEnum } from '@app/shared/enums/providerNetworkTypeEnum';
import { FeatureFlagContext } from '@app/feature-flags/FeatureFlagContext';
import Features from '@app/feature-flags/features';
import ClaimsActivityLineOfBusinessFilter from '@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityLineOfBusinessFilter';
import { Accordion } from '@app/shared/Accordion';
import { AccordionContainer } from '@app/shared/AccordionContainer';
import MultiselectDropdown from '@app/shared/checkbox-dropdown/MultiselectDropdown';
import MultiselectNestedDropdown from '@app/shared/checkbox-dropdown/MultiselectNestedDropdown';
import { formatDateCustom } from '@app/shared/formatDateTime';
import { QESButton } from '@app/shared/qes-standards/Button';
import { QESSelect } from '@app/shared/qes-standards/forms/Select';
import TooltipWrapper from '@app/shared/TooltipWrapper';
import { uniq } from "lodash";

const FilterControl = ({ clearAllFilters, createMap, ignoreInMapExport, position }: FilterControlProps): JSX.Element => {
  const filterContext = useContext(FilterContext);
  const mapDataContext = useContext(NetworkCoverageMapDataContext);
  const projectContext = useContext(CountyProjectContext);

  const adequacyTimeslices = filterContext.adequacyTimeslices;

  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  const selectedTimeslice = filterContext.selectedTimeslice ?? "";

  const sortedServiceAreaOptions: NestingParentDropdownItem[] = useMemo(() => {
    return filterContext.serviceArea.map(state => ({
      checkboxStatus: CheckboxStateEnum.Unchecked,
      children: state.subRegions.slice().sort((a, b) => a.displayName.localeCompare(b.displayName)).map(subRegion => {
        return {
          display: (
            <span>
              {subRegion.displayName}
            </span>
          ),
          key: subRegion.regionSetId.toString(),
          name: subRegion.displayName,
        }
      }),
      key: state.regionCode,
      name: state.displayName,
    })).slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [filterContext.serviceArea]);

  const maxTimeslices = useMemo(() => {
    return adequacyTimeslices.find(ts => !ts.datePublished) ? MAX_TIMESLICES_WITH_UNPUBLISHED : MAX_TIMESLICES;
  }, [adequacyTimeslices]);

  const renderAdequacyTimesliceOptions = () => (
    adequacyTimeslices
      ?.filter(ts =>
        !ts.isProjected &&
        !ts.isTermed &&
        !ts.isProjectedTermed &&
        ts.projectRunConfigurationType !== ProjectRunConfigurationType.DualMembershipSupplemental
      )
      .slice()
      .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
      .slice(0, maxTimeslices)
      .map(ts => (
        <option key={ts.projectRunConfigurationId} value={ts.projectRunConfigurationId}>
          {(!ts.datePublished ? "Unpublished " : "")}
          {ts.dateCreated ? formatDateCustom(ts.dateCreated, "MMM D, 'YY hh:mmA") : "Unknown Date"}
        </option>
      ))
  );

  const getEffectivenessChecked = (value: number): boolean => {
    return mapDataContext.selectedEffectiveness.includes(value);
  }

  const getEfficiencyChecked = (value: number): boolean => {
    return mapDataContext.selectedEfficiency.includes(value);
  }

  const getSelectedServiceAreaName = useMemo(() => {
    return sortedServiceAreaOptions
      .flatMap(item => item.children)
      .find(c => c.key === filterContext.selectedServiceArea)?.name ?? '';
  }, [filterContext.selectedServiceArea, sortedServiceAreaOptions]);

  const getMutuallyExclusiveCountyStatuses = (
    selectedItems: string[],
    currentStatuses: RegionSetStatusType[]
  ): RegionSetStatusType[] => {
    if (shouldRemoveOpportunity(currentStatuses, selectedItems)) {
      return removeOpportunity(selectedItems);
    }
    if (shouldSelectOnlyOpportunity(selectedItems)) {
      return [RegionSetStatusType.Opportunity];
    }
    return selectedItems as RegionSetStatusType[];
  };

  const shouldRemoveOpportunity = (
    currentStatuses: RegionSetStatusType[],
    selectedItems: string[]
  ): boolean =>
    currentStatuses.length === 1 &&
    currentStatuses[0] === RegionSetStatusType.Opportunity &&
    selectedItems.length > 1;

  const removeOpportunity = (selectedItems: string[]): RegionSetStatusType[] =>
    selectedItems.filter(item => item !== RegionSetStatusType.Opportunity) as RegionSetStatusType[];

  const shouldSelectOnlyOpportunity = (selectedItems: string[]): boolean =>
    selectedItems.includes(RegionSetStatusType.Opportunity) && selectedItems.length > 1;

  const visibleCountyStatusOptions = filterContext.countyStatusOptions.filter(
    opt =>
      filterContext.availableCountyStatuses &&
      filterContext.availableCountyStatuses.includes(opt.key as RegionSetStatusType)
  );

  const buildCountyStatusChildren = (
    options: typeof visibleCountyStatusOptions,
    selected: RegionSetStatusType[]
  ) =>
    options
      .map(opt => ({
        checkboxStatus: selected.includes(opt.key as RegionSetStatusType)
          ? CheckboxStateEnum.Checked
          : CheckboxStateEnum.Unchecked,
        key: opt.key,
        name: opt.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

  const countyStatusItems: NestingParentDropdownItem[] = [
    ...(
      visibleCountyStatusOptions.length > 0
        ? [{
          checkboxStatus: CheckboxStateEnum.Unchecked,
          children: buildCountyStatusChildren(visibleCountyStatusOptions, filterContext.selectedCountyStatuses),
          key: "current",
          name: "Current Service Area",
        }]
        : []
    ),
    ...(
      filterContext.availableCountyStatuses &&
        filterContext.availableCountyStatuses.includes(RegionSetStatusType.Opportunity)
        ? [{
          checkboxStatus: filterContext.selectedCountyStatuses.includes(RegionSetStatusType.Opportunity)
            ? CheckboxStateEnum.Checked
            : CheckboxStateEnum.Unchecked,
          children: [],
          key: RegionSetStatusType.Opportunity,
          name: "Opportunity to Expand",
        }]
        : []
    ),
  ];

  const sortedProviders: NestingParentDropdownItem[] = useMemo(() => {
    return filterContext.providerSource.map((provider) => ({
      checkboxStatus: CheckboxStateEnum.Unchecked,
      children: provider.children?.map((child: { type: string; name: string }) => ({
        key: child.type,
        name: child.name,
      })) || [],
      key: provider.type || provider.name.toLowerCase().replace(/\s+/g, '-'), // Use a fallback key
      name: provider.name,
    }));
  }, [filterContext.providerSource]);

  const selectedRunConfigurationId = selectedTimeslice || projectContext.runConfiguration?.projectRunConfigurationId;

  const { primaryRunConfiguration, secondaryRunConfiguration } = useMemo(
    () => getPrimaryAndSecondaryRunConfigurations(
      projectContext.project.projectRunConfigurations,
      selectedRunConfigurationId
    ),
    [projectContext.project.projectRunConfigurations, selectedRunConfigurationId]
  );

  const selectedMembership = selectedRunConfigurationId === secondaryRunConfiguration?.projectRunConfigurationId
    ? NetworkCoverageMapMembershipType.Secondary
    : NetworkCoverageMapMembershipType.Primary;

  // Applies a run configuration change made from the map through setDualMembershipMode -- the same
  // call the Adequacy and Network Integrity membership selectors use -- so runConfiguration,
  // compareToRunConfiguration and isDualMembershipMode stay coherent. A bare setRunConfiguration
  // leaves the previous compare-to behind, which hides the Maps tab mid-session and strips the
  // membership selector off the Adequacy tab. FilterContext syncs selectedTimeslice back from the
  // project context, so the map still refetches without a second write.
  const applyRunConfiguration = (runConfiguration?: ProjectRunConfiguration) => {
    if (!runConfiguration) return;

    // Move the dropdowns on click rather than waiting on the awaited load below, which also lets
    // the map's service-area fetch run alongside it.
    filterContext.handleSelectedTimesliceChange?.(runConfiguration.projectRunConfigurationId);

    projectContext.setDualMembershipMode(
      false,
      runConfiguration.projectRunConfigurationType,
      runConfiguration.projectRunConfigurationId,
      undefined,
      undefined,
      // The map is a single-run, single-membership view -- never let a Standard -> dual membership
      // move synthesize a compare-to, which would hide the Maps tab out from under the user
      true,
    );
    clearAllFilters();
  };

  const applyTimesliceForMembership = (primaryRunConfigurationId: number, membershipType: NetworkCoverageMapMembershipType) => {
    if (!projectContext.project.projectRunConfigurations.some(prc => prc.projectRunConfigurationId === primaryRunConfigurationId)) return;

    const pair = getPrimaryAndSecondaryRunConfigurations(
      projectContext.project.projectRunConfigurations,
      primaryRunConfigurationId
    );

    applyRunConfiguration(membershipType === NetworkCoverageMapMembershipType.Secondary && pair.secondaryRunConfiguration
      ? pair.secondaryRunConfiguration
      : pair.primaryRunConfiguration);
  };

  const handleMembershipTypeChange = (membershipType: NetworkCoverageMapMembershipType) => {
    applyTimesliceForMembership(primaryRunConfiguration?.projectRunConfigurationId, membershipType);
  };

  const handleAdequacyTimesliceChange = (primaryRunConfigurationId: number) => {
    applyTimesliceForMembership(primaryRunConfigurationId, selectedMembership);
  };

  const container = useMapboxControl({ mapRef: mapDataContext.mapRef, position });
  if (!container) return null;

  const formatCountyFilterString = (regionIds: string[]): string =>
    regionIds.map(id => filterContext.serviceAreaRegionIdToRegionCodeMap.get(id).countyCode).toString();

  return createPortal(
    <div {...(ignoreInMapExport ? { 'data-html2canvas-ignore': '' } : {})}>
      <div
        className="col-12 col-md-auto network-coverage-map__filter-container"
        style={{
          height: `${mapDataContext.mapRef.current?.getCanvas().getBoundingClientRect().height}px`,
        }}
      >
        {filterContext.showAdvancedComparison &&
          <div className="col-12 col-md-auto pt-2 px-2">
            <QESSelect
              allowEmpty={false}
              className="network-coverage-map__filter-adequacy-time-slice"
              isValid={true}
              label="Results As Of"
              onChange={e => handleAdequacyTimesliceChange(Number(e.target.value))}
              testId="network-coverage-map__filter--adequacy-time-slice"
              // The options are keyed by primary run configuration id, so a Secondary selection has
              // to resolve back to its primary to stay highlighted.
              value={primaryRunConfiguration?.projectRunConfigurationId ?? selectedTimeslice}
            >
              {renderAdequacyTimesliceOptions()}
            </QESSelect>
          </div>
        }
        {featureFlags.has(Features.DUAL_MEMBERSHIP_FOR_MAPS) && !!secondaryRunConfiguration && (
          <div className="col-12 col-md-auto pt-2 px-2">
            <NetworkCoverageMapMembershipTypeFilter
              membershipType={selectedMembership}
              setMembershipType={handleMembershipTypeChange}
              primaryMembershipAlias={primaryRunConfiguration?.memberSetAlias}
              secondaryMembershipAlias={secondaryRunConfiguration?.memberSetAlias}
            />
          </div>
        )}
        <div className="col-12 col-md-auto pt-2 px-2">
          <MultiselectNestedDropdown
            items={countyStatusItems}
            containerClassName="w-100"
            label="County Status"
            hideSelectAll
            hideSearch
            hideParentCheckboxes={false}
            parentsAsValues
            keepParentOpen
            selectedItems={filterContext.selectedCountyStatuses}
            onSelectionChanged={(selectedItems: string[]) => {
              const newStatuses = getMutuallyExclusiveCountyStatuses(selectedItems, filterContext.selectedCountyStatuses);
              filterContext.setSelectedCountyStatuses(newStatuses);
              // reset service area filter
              filterContext.replaceFilterUrl(FilterType.State, filterContext.selectedServiceArea, '');
              filterContext.replaceFilterUrl(FilterType.County, filterContext.selectedServiceArea, '');
              filterContext.setSelectedServiceArea('');
              filterContext.setSelectedServiceAreas([]);
            }}
            testId="network-coverage-map__filter--county-status"
          />
        </div>
        <div className='col-12 col-md-auto pt-2 px-2'>
          <MultiselectNestedDropdown
            customSelectInputText={featureFlags.has(Features.SERVICE_AREA_WIDE_MAPPING_COUNTY) ? null : getSelectedServiceAreaName}
            items={sortedServiceAreaOptions}
            containerClassName='w-100'
            hideSelectAll
            label="Service Area"
            hideParentCheckboxes={!featureFlags.has(Features.SERVICE_AREA_WIDE_MAPPING_COUNTY)}
            onSelectionChanged={(selectedItems) => {
              if (featureFlags.has(Features.SERVICE_AREA_WIDE_MAPPING_COUNTY))
              {
                const current = filterContext.serviceAreaRegionIdToRegionCodeMap.get(filterContext.selectedServiceAreas[0]);
                const selectedStates = uniq(selectedItems.map(i => filterContext.serviceAreaRegionIdToRegionCodeMap.get(i)).map(i => i.stateCode));
                
                if (selectedStates?.length > 1 || !current) {
                  const newState = selectedStates.filter(s => current?.stateCode !== s)[0];
                  filterContext.replaceFilterUrl(FilterType.State, current?.stateCode, newState);

                  const selected = selectedItems.filter(i => filterContext.serviceAreaRegionIdToRegionCodeMap.get(i).stateCode === newState);
                  filterContext.replaceFilterUrl(FilterType.County, formatCountyFilterString(filterContext.selectedServiceAreas), formatCountyFilterString(selected));
                  filterContext.setSelectedServiceAreas(selected);
                  //TODO 208905: Remove this when adding provider/members
                  filterContext.setSelectedServiceArea(selected[selected.length - 1] ?? '');
                }
                else
                {
                  if (selectedStates.length === 0)
                    filterContext.replaceFilterUrl(FilterType.State, current?.stateCode, '');

                  filterContext.replaceFilterUrl(FilterType.County, formatCountyFilterString(filterContext.selectedServiceAreas), formatCountyFilterString(selectedItems));
                  filterContext.setSelectedServiceAreas(selectedItems);
                  //TODO 208905: Remove this when adding provider/members
                  filterContext.setSelectedServiceArea(selectedItems[selectedItems.length - 1] ?? '');
                }
              }
              else
              {
                const current = filterContext.serviceAreaRegionIdToRegionCodeMap.get(filterContext.selectedServiceArea);
                const selected = selectedItems.length > 0
                  ? filterContext.serviceAreaRegionIdToRegionCodeMap.get(selectedItems[selectedItems.length - 1])
                  : null;
                filterContext.replaceFilterUrl(FilterType.State, current?.stateCode, selected?.stateCode);
                filterContext.replaceFilterUrl(FilterType.County, current?.countyCode, selected?.countyCode);
                filterContext.setSelectedServiceArea(selectedItems[selectedItems.length - 1] ?? '');
              }
            }}
            selectedItems={featureFlags.has(Features.SERVICE_AREA_WIDE_MAPPING_COUNTY) ? filterContext.selectedServiceAreas : filterContext.selectedServiceArea ? [filterContext.selectedServiceArea] : []}
            testId="network-coverage-map__filter--service-area"
          />
        </div>
        <div className="col-12 col-md-auto pt-2 px-2">
          <TooltipWrapper
            text={filterContext.getSpecialtyTooltipText()}
            position={"right"}
            triggerWidth={"100%"}
            style={{ width: "100%" }}
          >
            <SingleSelectDropdown
              label="Specialty"
              containerClassName='w-100 network-coverage-map__filter-specialty'
              items={filterContext.sortedSpecialtyGroupCodes}
              testId="network-coverage-map__filter--specialty"
              onSelectionChanged={(selectedItem) => {
                filterContext.replaceFilterUrl(FilterType.Specialty, filterContext.selectedSpecialtyGroupCode.key, selectedItem.key as string);
                filterContext.setSelectedSpecialtyGroupCode({ key: selectedItem.key, name: selectedItem.name });
              }}
              selectedItem={filterContext.selectedSpecialtyGroupCode}
            />
          </TooltipWrapper>
        </div>
        <div className="col-12 col-md-auto pt-2 px-2">
          <MultiselectNestedDropdown
            items={sortedProviders}
            containerClassName="w-100"
            label="Provider Source"
            hideParentCheckboxes={false}
            parentsAsValues
            keepParentOpen
            hideSelectAll
            hideSearch={true}
            onSelectionChanged={(selectedItems: string[]) => {
              filterContext.setSelectedProviderSources(
                selectedItems.map(item => item as ProviderNetworkTypeEnum)
              );
            }}
            selectedItems={filterContext.selectedProviderSources}
            testId="network-coverage-map__filter--provider-source"
          />
        </div>
          <div className="col-12 col-md-auto pt-2 px-2">
            <AccordionContainer className="mt-3">
              <Accordion
                className="network-coverage-map__filter-advanced-filters-accordion"
                title="Advanced Filters" defaultOpen={false}
                testId="network-coverage-map__filter--advanced-filters-accordion"
              >
                <AccordionContainer>
                  <Accordion
                    className="network-coverage-map__filter-advanced-filters-sub-accordion"
                    title="Provider Contribution Measures" defaultOpen={true}
                    testId="network-coverage-map__filter--provider-contribution-measures-accordion"
                  >
                    <DualRangeSlider
                      testId={"network-coverage-map__filter--serves-percent-slider"}
                      label={"Serves %"}
                      minValue={ZERO_PERCENT_FILTER}
                      maxValue={HUNDRED_PERCENT_FILTER}
                      onChange={([min, max]) => {
                        mapDataContext.setFilterServesMinPercent(min);
                        mapDataContext.setFilterServesMaxPercent(max);
                      }}
                      value={[mapDataContext.filterServesMinPercent, mapDataContext.filterServesMaxPercent]}
                    />
                    <hr/>
                    <DualRangeSlider
                      testId={"network-coverage-map__filter--gains-percent-slider"}
                      label={"Gains %"}
                      minValue={ZERO_PERCENT_FILTER}
                      maxValue={HUNDRED_PERCENT_FILTER}
                      onChange={([min, max]) => {
                        mapDataContext.setFilterGainsMinPercent(min);
                        mapDataContext.setFilterGainsMaxPercent(max);
                      }}
                      value={[mapDataContext.filterGainsMinPercent, mapDataContext.filterGainsMaxPercent]}
                    />
                    <hr/>
                    <DualRangeSlider
                      testId={"network-coverage-map__filter--impact-percent-slider"}
                      label={"Impact %"}
                      minValue={ZERO_PERCENT_FILTER}
                      maxValue={HUNDRED_PERCENT_FILTER}
                      onChange={([min, max]) => {
                        mapDataContext.setFilterImpactsMinPercent(min);
                        mapDataContext.setFilterImpactsMaxPercent(max);
                      }}
                      value={[mapDataContext.filterImpactsMinPercent, mapDataContext.filterImpactsMaxPercent]}
                    />
                  </Accordion>
                  {adequacyTimeslices.find(x => x.projectRunConfigurationId == selectedTimeslice).showPciData ?
                    (
                      <Accordion
                        className="network-coverage-map__filter-advanced-filters-sub-accordion"
                        title="Provider Claims Insights"
                        testId="network-coverage-map__filter--provider-claims-insights-accordion"
                      >
                        <ClaimsActivityLineOfBusinessFilter
                          disabled={false}
                          selectedLineOfBusiness={mapDataContext.selectedLineOfBusiness}
                          setSelectedLineOfBusiness={mapDataContext.setSelectedLineOfBusiness}
                          testId="network-coverage-map__filter--line-of-business"
                          project={projectContext.project}
                          allowEmpty={true}
                        />
                        <div className="mb-2 mt-2" />
                        <MultiselectDropdown
                          disabled={!mapDataContext.selectedLineOfBusiness}
                          testId="network-coverage-map__filter--volume-indicator"
                          label="Volume Indicator"
                          containerClassName="w-100 network-coverage-map__filter-advanced-filters-volume-indicator"
                          customSelectInputText={
                            !mapDataContext.selectedVolumeIndicators.length
                              ? "All"
                              : null
                          }
                          items={filterContext.mappedVolumeIndicators}
                          selectedItems={mapDataContext.selectedVolumeIndicators}
                          onSelectionChanged={(items) => {
                            mapDataContext.setSelectedVolumeIndicators(items)
                          }}
                        />
                        <div className="floating-filters-menu-separator mb-4 mt-4" />
                        <RoundCheckbox
                          includesUnknown={true}
                          label={
                            <span>Quality (Effectiveness) Score</span>
                          }
                          values={filterContext.effectivenessEfficiencyValues}
                          testId="effectiveness"
                          isChecked={getEffectivenessChecked}
                          onChange={(value: number) => {
                            mapDataContext.setSelectedEffectiveness(prev =>
                              prev.includes(value)
                                ? prev.filter(v => v !== value)
                                : [...prev, value]
                            );
                          }}
                        />
                        <div className="mb-2 mt-2" />
                        <RoundCheckbox
                          includesUnknown={true}
                          label={
                            <span>Cost (Efficiency) Score</span>
                          }
                          values={filterContext.effectivenessEfficiencyValues}
                          testId="efficiency"
                          isChecked={getEfficiencyChecked}
                          onChange={(value: number) => {
                            mapDataContext.setSelectedEfficiency(prev =>
                              prev.includes(value)
                                ? prev.filter(v => v !== value)
                                : [...prev, value]
                            );
                          }}
                        />
                      </Accordion>
                    ) : null
                  }
                </AccordionContainer>
              </Accordion>
            </AccordionContainer>
          </div>
        <span className="d-flex justify-content-end col-12 col-xl pb-2 network-coverage-map__filter-action-buttons">
          <QESButton
            qesType="secondary"
            disabled={filterContext.mapButtonsAreDisabled}
            onClick={createMap}
            qesTestId="network-coverage-map__generate"
          >
            Create Map
          </QESButton>
          <QESButton
            qesType="secondary"
            qesTestId="network-coverage-map__clear-all"
            disabled={filterContext.mapButtonsAreDisabled}
            onClick={clearAllFilters}
          >
            Clear All
          </QESButton>
        </span>
      </div>
    </div>,
    container
  )
}

export default memo(FilterControl);